import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getStoredUtm, trackFunnelEvent, getAttribution } from '@/utils/tracking';
import { getDeviceBucket } from '@/utils/deviceBucket';

const API = '/api';
const FLOW_NAME = 'energy_control_quickscan';

// 3 simple questions vóór demo — commercial cut Feb 2026.
// Living / household_size / postcode4 zijn verplaatst naar DeepDive (post-demo).
const STEP_LABELS = {
  1: 'goal',
  2: 'solar',
  3: 'appliances',
};

const INITIAL = {
  goal: '',
  solar: '',
  appliances: [],          // multi-select
  // Onderstaande blijven in state aanwezig zodat ze later via DeepDive
  // kunnen worden ingevuld zonder schema-mismatch.
  living: '',
  household_size: '',
  postcode4: '',
};

// Step 3 (appliances) mag leeg blijven ('Geen' / 'Weet ik niet').
const REQUIRED = {
  1: ['goal'],
  2: ['solar'],
  3: [],
};

/**
 * useEnergyControlQuickScan
 * --------------------------------------------------------------------- *
 * State machine for the 5-question Quick Scan + inline Demo Experience.
 *
 * Flow stages:
 *   stage = 'quickscan'    – user is filling in the 5 questions
 *   stage = 'demo'         – user has submitted, demo payload is rendering
 *   stage = 'deepdive'     – user opted into the optional DeepDive
 *   stage = 'deepdive_done'– DeepDive has been submitted
 *
 * Funnel events:
 *   quickscan_started, quickscan_step_completed, quickscan_completed,
 *   demo_viewed, demo_module_viewed, demo_cta_clicked,
 *   deepdive_started, deepdive_completed
 *
 * SessionStorage guards prevent React StrictMode / hot-reload double-fire.
 */
export default function useEnergyControlQuickScan() {
  const [stage, setStage] = useState('quickscan');
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [quickscanId, setQuickscanId] = useState(null);
  const [demo, setDemo] = useState(null);
  const [deepdiveLoading, setDeepdiveLoading] = useState(false);

  // Auto-advance guard — only single-select steps (1 & 2). Once we have
  // auto-advanced for a given step, do NOT re-trigger when the user navigates
  // back and re-views their existing selection. The lock is per session.
  const autoAdvancedRef = useRef({});

  const totalSteps = 3;
  const progress = Math.round((currentStep / totalSteps) * 100);

  // Fire once-per-session `quickscan_started`.
  useEffect(() => {
    try {
      const sid = sessionStorage.getItem('powersure_session') || '';
      const guardKey = `ps_quickscan_started_${sid}`;
      if (!sessionStorage.getItem(guardKey)) {
        trackFunnelEvent('quickscan_started', {
          flow: FLOW_NAME,
          meta: { flow: FLOW_NAME, device_type: getDeviceBucket() },
        });
        sessionStorage.setItem(guardKey, '1');
      }
    } catch { /* ignore */ }
  }, []);

  // Jun 2026 P0 — granular drop-off instrumentation. Fires once per step per
  // session: `quickscan_step_viewed`. NO PII. Only step number, label, and
  // device type. Used to pinpoint where the 86% drop happens.
  useEffect(() => {
    if (stage !== 'quickscan') return;
    try {
      const sid = sessionStorage.getItem('powersure_session') || '';
      const guardKey = `ps_qs_step_viewed_${sid}_${currentStep}`;
      if (!sessionStorage.getItem(guardKey)) {
        trackFunnelEvent('quickscan_step_viewed', {
          step: currentStep,
          flow: FLOW_NAME,
          meta: {
            flow: FLOW_NAME,
            step: currentStep,
            current_step_label: STEP_LABELS[currentStep] || `step_${currentStep}`,
            total_steps: totalSteps,
            device_type: getDeviceBucket(),
          },
        });
        sessionStorage.setItem(guardKey, '1');
      }
    } catch { /* ignore */ }
  }, [stage, currentStep, totalSteps]);

  // Jun 2026 P0 — `quickscan_abandoned` on unload IF user is still in the
  // quickscan stage and has not completed all steps. Best-effort; no PII.
  useEffect(() => {
    if (stage !== 'quickscan') return;
    const onUnload = () => {
      try {
        const sid = sessionStorage.getItem('powersure_session') || '';
        const guardKey = `ps_qs_abandoned_${sid}`;
        if (sessionStorage.getItem(guardKey)) return;
        sessionStorage.setItem(guardKey, '1');
        const body = JSON.stringify({
          event: 'quickscan_abandoned',
          session_id: sid,
          step: currentStep,
          metadata: {
            flow: FLOW_NAME,
            last_step: currentStep,
            total_steps: totalSteps,
            current_step_label: STEP_LABELS[currentStep] || `step_${currentStep}`,
            device_type: getDeviceBucket(),
          },
        });
        // navigator.sendBeacon survives page unload; falls back to fetch keepalive.
        const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/track/funnel`;
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        } else {
          fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true })
            .catch(() => { /* silent */ });
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
    };
  }, [stage, currentStep, totalSteps]);

  const update = (patch) => {
    setAnswers((prev) => ({ ...prev, ...patch }));
    setErrors({});
  };

  const toggleAppliance = (value) => {
    setAnswers((prev) => {
      const arr = prev.appliances || [];
      // "none" and "unknown" are exclusive — clearing the rest.
      if (value === 'none' || value === 'unknown') {
        return { ...prev, appliances: arr.includes(value) ? [] : [value] };
      }
      // Selecting any real appliance clears "none"/"unknown".
      const cleaned = arr.filter((v) => v !== 'none' && v !== 'unknown');
      const next = cleaned.includes(value)
        ? cleaned.filter((v) => v !== value)
        : [...cleaned, value];
      return { ...prev, appliances: next };
    });
  };

  const validate = () => {
    const required = REQUIRED[currentStep] || [];
    const newErrors = {};
    for (const f of required) {
      const v = answers[f];
      if (v === '' || v === null || v === undefined) newErrors[f] = 'Maak een keuze om verder te gaan';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      trackFunnelEvent('validation_failed', {
        step: currentStep,
        flow: FLOW_NAME,
        meta: { flow: FLOW_NAME, step: currentStep, current_step_label: STEP_LABELS[currentStep] || `step_${currentStep}`, failed_fields: Object.keys(newErrors) },
      });
    }
    return Object.keys(newErrors).length === 0;
  };

  const fireStepCompleted = (stepIndex, extraMeta = {}) => {
    try {
      const sid = sessionStorage.getItem('powersure_session') || '';
      const guardKey = `ps_qs_step_completed_${sid}_${stepIndex}`;
      if (!sessionStorage.getItem(guardKey)) {
        trackFunnelEvent('quickscan_step_completed', {
          step: stepIndex,
          flow: FLOW_NAME,
          meta: {
            flow: FLOW_NAME,
            step: stepIndex,
            current_step_label: STEP_LABELS[stepIndex] || `step_${stepIndex}`,
            total_steps: totalSteps,
            device_type: getDeviceBucket(),
            ...extraMeta,
          },
        });
        sessionStorage.setItem(guardKey, '1');
      }
    } catch {
      trackFunnelEvent('quickscan_step_completed', { step: stepIndex, flow: FLOW_NAME });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      trackFunnelEvent('step_back_clicked', {
        step: currentStep,
        flow: FLOW_NAME,
        meta: { flow: FLOW_NAME, step: currentStep, current_step_label: STEP_LABELS[currentStep] || `step_${currentStep}` },
      });
      setCurrentStep(currentStep - 1);
    }
    setErrors({});
  };

  const submitQuickScan = useCallback(async () => {
    setLoading(true);
    // Jun 2026 P0 — explicit lifecycle event for the plan-generation step.
    try {
      trackFunnelEvent('optimal_plan_generation_started', {
        flow: FLOW_NAME,
        meta: { flow: FLOW_NAME, device_type: getDeviceBucket() },
      });
    } catch { /* ignore */ }
    try {
      const utm = getStoredUtm();
      const attr = getAttribution();
      const sid = (() => {
        try { return sessionStorage.getItem('powersure_session') || ''; } catch { return ''; }
      })();
      const payload = {
        goal: answers.goal || null,
        solar: answers.solar || null,
        appliances: answers.appliances || [],
        living: answers.living || null,
        household_size: answers.household_size ? Number(answers.household_size) : null,
        postcode4: answers.postcode4 || null,
        session_id: sid,
        utm_source: utm.utm_source || '',
        utm_medium: utm.utm_medium || '',
        utm_campaign: utm.utm_campaign || '',
        utm_content: utm.utm_content || '',
        utm_term: utm.utm_term || '',
        gclid: attr.gclid || '',
        gbraid: attr.gbraid || '',
        wbraid: attr.wbraid || '',
        landing_url: attr.landing_url || '',
        referrer: attr.referrer || '',
      };
      const res = await axios.post(`${API}/energy-control/quickscan`, payload);
      if (res?.data?.success) {
        const newId = res.data.quickscan_id;
        setQuickscanId(newId);
        setDemo(res.data.demo);
        setStage('demo');
        // Funnel: completion + demo_viewed (StrictMode-guarded).
        try {
          const guard = `ps_quickscan_completed_${sid}_${newId}`;
          if (!sessionStorage.getItem(guard)) {
            trackFunnelEvent('quickscan_completed', {
              intake_id: newId, step: totalSteps, flow: FLOW_NAME,
              meta: {
                flow: FLOW_NAME, step: totalSteps, intake_id: newId,
                total_steps: totalSteps, device_type: getDeviceBucket(),
              },
            });
            trackFunnelEvent('demo_viewed', {
              intake_id: newId, flow: FLOW_NAME,
              meta: {
                flow: FLOW_NAME, intake_id: newId,
                source: 'inline_after_quickscan', device_type: getDeviceBucket(),
              },
            });
            sessionStorage.setItem(guard, '1');
          }
        } catch { /* ignore */ }
        if (window.pwTrack) window.pwTrack('energy_control_quickscan_complete', { quickscan_id: newId });
      } else {
        // Jun 2026 P0 — explicit failure tracking.
        try {
          trackFunnelEvent('optimal_plan_generation_failed', {
            flow: FLOW_NAME,
            meta: { flow: FLOW_NAME, reason: 'api_no_success', device_type: getDeviceBucket() },
          });
        } catch { /* ignore */ }
        toast.error('Er ging iets mis. Probeer het opnieuw.');
      }
    } catch (err) {
      // Jun 2026 P0 — explicit failure tracking. NO PII, only safe error code.
      try {
        const code = err?.response?.status || 'network_error';
        trackFunnelEvent('optimal_plan_generation_failed', {
          flow: FLOW_NAME,
          meta: { flow: FLOW_NAME, reason: String(code), device_type: getDeviceBucket() },
        });
      } catch { /* ignore */ }
      toast.error('Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  }, [answers]);

  const handleNext = () => {
    if (!validate()) {
      toast.error('Controleer de verplichte velden');
      return;
    }
    fireStepCompleted(currentStep, { value: answers[STEP_LABELS[currentStep]] || '' });
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      submitQuickScan();
    }
  };

  // Jun 2026 — fast-path skip from step 3 (multi-select) directly to demo.
  // Triggered by the prominent "Toon mijn demo direct" button on step 3 to
  // unblock users who don't want to fill in the appliance list. Fires a
  // dedicated tracking event so we can measure how often this path is used.
  const skipStep3ToDemo = useCallback(() => {
    if (currentStep !== totalSteps) return;
    try {
      trackFunnelEvent('quickscan_skip_clicked', {
        step: currentStep,
        flow: FLOW_NAME,
        meta: {
          flow: FLOW_NAME,
          step: currentStep,
          current_step_label: STEP_LABELS[currentStep] || `step_${currentStep}`,
          device_type: getDeviceBucket(),
          skip_path: 'step3_appliances_to_demo',
        },
      });
    } catch { /* ignore */ }
    submitQuickScan();
  }, [currentStep, totalSteps, submitQuickScan]);

  // Jun 2026 — autonomous commercial tuning. Auto-advance on single-select
  // steps (1: goal, 2: solar) to halve required clicks and reduce mobile
  // friction. Step 3 (appliances, multi-select) stays manual so users can
  // toggle multiple before committing. Auto-advance fires at most ONCE per
  // step per session — if the user navigates back via the Terug button and
  // re-views the same selection, we do NOT auto-advance again.
  useEffect(() => {
    if (stage !== 'quickscan') return;
    if (loading) return;
    if (currentStep !== 1 && currentStep !== 2) return;
    const field = STEP_LABELS[currentStep];
    const val = answers[field];
    if (!val) return;
    if (autoAdvancedRef.current[currentStep]) return;
    autoAdvancedRef.current[currentStep] = true;
    // Small delay so the user sees their selection highlight before transition.
    const t = setTimeout(() => {
      if (!validate()) return;
      fireStepCompleted(currentStep, { value: val, auto_advanced: true });
      if (currentStep < totalSteps) {
        setCurrentStep((s) => s + 1);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [answers.goal, answers.solar, currentStep, stage, loading]);

  const trackModuleViewed = (moduleName) => {
    if (!quickscanId) return;
    try {
      const sid = sessionStorage.getItem('powersure_session') || '';
      const guard = `ps_demo_mod_${sid}_${quickscanId}_${moduleName}`;
      if (sessionStorage.getItem(guard)) return;
      sessionStorage.setItem(guard, '1');
      trackFunnelEvent('demo_module_viewed', {
        intake_id: quickscanId, flow: FLOW_NAME,
        meta: { flow: FLOW_NAME, intake_id: quickscanId, module: moduleName },
      });
    } catch { /* ignore */ }
  };

  const trackDemoCtaClicked = (ctaLabel, ctaType) => {
    trackFunnelEvent('demo_cta_clicked', {
      intake_id: quickscanId || '',
      flow: FLOW_NAME,
      meta: {
        flow: FLOW_NAME,
        intake_id: quickscanId || '',
        cta_label: ctaLabel,
        cta_type: ctaType,
      },
    });
  };

  const startDeepDive = () => {
    setStage('deepdive');
    trackFunnelEvent('deepdive_started', {
      intake_id: quickscanId || '',
      flow: FLOW_NAME,
      meta: { flow: FLOW_NAME, intake_id: quickscanId || '' },
    });
  };

  const submitDeepDive = async (dd) => {
    if (!quickscanId) return false;
    setDeepdiveLoading(true);
    try {
      const payload = {
        quickscan_id: quickscanId,
        contract_type: dd.contract_type || null,
        control_preference: dd.control_preference || null,
        installation_preference: dd.installation_preference || null,
        monitoring_interest: dd.monitoring_interest || null,
        email: dd.email || null,
      };
      const res = await axios.post(`${API}/energy-control/deepdive`, payload);
      if (res?.data?.success) {
        trackFunnelEvent('deepdive_completed', {
          intake_id: quickscanId, flow: FLOW_NAME,
          meta: { flow: FLOW_NAME, intake_id: quickscanId },
        });
        setStage('deepdive_done');
        return true;
      }
    } catch {
      toast.error('Verzenden mislukt. Probeer het opnieuw.');
    } finally {
      setDeepdiveLoading(false);
    }
    return false;
  };

  return {
    stage,
    currentStep,
    totalSteps,
    progress,
    answers,
    errors,
    loading,
    quickscanId,
    demo,
    deepdiveLoading,
    update,
    toggleAppliance,
    handleNext,
    handleBack,
    skipStep3ToDemo,
    trackModuleViewed,
    trackDemoCtaClicked,
    startDeepDive,
    submitDeepDive,
  };
}
